import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { submitLead } from '@/utils/submitLead';
import { useToast } from '@/hooks/use-toast';
import { sendWhatsAppNotification } from '@/utils/whatsappNotification';
import { trackLead } from '@/utils/analytics';

const LeadCaptureSection = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    nationality: '',
    programInterest: '',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim()) {
      toast({
        title: 'Error',
        description: 'Please fill in at least your name and email.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    // Open WhatsApp FIRST, synchronously — popup blockers reject calls made
    // after `await`.
    sendWhatsAppNotification({
      name: formData.name,
      email: formData.email,
      phone: formData.phone || undefined,
      nationality: formData.nationality || undefined,
      programInterest: formData.programInterest || undefined,
      message: formData.message || undefined,
      source: 'lead_capture',
    });

    try {
      await submitLead({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        nationality: formData.nationality || undefined,
        programInterest: formData.programInterest || undefined,
        message: formData.message || undefined,
        source: 'lead_capture',
        language: i18n.language,
      });

      trackLead('lead_capture');
      toast({
        title: t('leadCapture.success'),
        description: t('leadCapture.successMessage'),
      });
      setFormData({ name: '', email: '', phone: '', nationality: '', programInterest: '', message: '' });
    } catch {
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-accent relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#112E4A] via-[#112E4A] to-[#0A1F3A]" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('leadCapture.title')}
            </h2>
            <p className="text-lg text-white/80">
              {t('leadCapture.subtitle')}
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10 space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-white/80 text-sm mb-1.5 block">{t('leadCapture.name')}</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('leadCapture.namePlaceholder')}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  maxLength={100}
                  required
                />
              </div>
              <div>
                <label className="text-white/80 text-sm mb-1.5 block">{t('leadCapture.email')}</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t('leadCapture.emailPlaceholder')}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  maxLength={255}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-white/80 text-sm mb-1.5 block">{t('leadCapture.phone')}</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder={t('leadCapture.phonePlaceholder')}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  maxLength={30}
                />
              </div>
              <div>
                <label className="text-white/80 text-sm mb-1.5 block">{t('leadCapture.nationality')}</label>
                <Input
                  value={formData.nationality}
                  onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                  placeholder={t('leadCapture.nationalityPlaceholder')}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  maxLength={100}
                />
              </div>
            </div>

            <div>
              <label className="text-white/80 text-sm mb-1.5 block">{t('leadCapture.programInterest')}</label>
              <Select
                value={formData.programInterest}
                onValueChange={(value) => setFormData({ ...formData, programInterest: value })}
              >
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder={t('leadCapture.programInterestPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bilingual Programs">{t('leadCapture.interestOptions.bilingual')}</SelectItem>
                  <SelectItem value="Day 1 CPT Programs">{t('leadCapture.interestOptions.day1cpt')}</SelectItem>
                  <SelectItem value="Masters Programs">{t('leadCapture.interestOptions.masters')}</SelectItem>
                  <SelectItem value="Bachelors Programs">{t('leadCapture.interestOptions.bachelors')}</SelectItem>
                  <SelectItem value="Other">{t('leadCapture.interestOptions.other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-white/80 text-sm mb-1.5 block">{t('leadCapture.message')}</label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder={t('leadCapture.messagePlaceholder')}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 min-h-[100px]"
                maxLength={2000}
              />
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 text-lg"
            >
              {isSubmitting ? t('leadCapture.submitting') : t('leadCapture.submit')}
              <Send className="ml-2 h-5 w-5" />
            </Button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default LeadCaptureSection;
